import type { AdminReportDetailDto } from '@carcheck/shared';
import CloseIcon from '@mui/icons-material/Close';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { ReportAcceptanceBadge } from './ReportAcceptanceBadge';
import { RedactedField } from '../../../components/pii';
import { tokens } from '../../../design';
import { reportTag, shortId } from '../_lib/displayName';

/**
 * Report detail-drawer header (AUT-127): rounded R-XXXX avatar + title
 * + inspector name (PII-gated) + acceptance badge + truncated id + close
 * button. Mirrors `OrderDetailHeader` so the two surfaces share visual
 * shape.
 *
 * AUT-284 — the chip is `ReportAcceptanceBadge`, not the deleted raw-status
 * one: the header used to contradict the acceptance bar two hand-spans
 * below it («ждёт заказчика» over «Принят заказчиком»).
 */
export const ReportDetailHeader = ({
  detail,
  onClose,
}: {
  detail: AdminReportDetailDto;
  onClose: () => void;
}): ReactElement => {
  const { t } = useTranslation();
  const inspectorName = detail.inspector.name;
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.75,
        p: '18px 24px',
        borderBottom: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Avatar
        variant="rounded"
        sx={{
          width: 52,
          height: 52,
          fontSize: 13,
          fontWeight: 700,
          fontFamily: 'monospace',
          bgcolor: 'info.light',
          color: 'info.dark',
        }}
      >
        {reportTag(detail.reportNumber)}
      </Avatar>
      <Box sx={{ minWidth: 0 }}>
        <Typography
          sx={{ fontSize: 20, fontWeight: 700, letterSpacing: '-0.3px' }}
          noWrap
          data-testid="report-detail-number"
        >
          {t('reports.detail.title', { number: `№ ${detail.reportNumber}` })}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.75, flexWrap: 'wrap' }}>
          {inspectorName ? (
            <Typography
              variant="body2"
              color="text.secondary"
              component="span"
              data-testid="report-detail-byline"
            >
              <RedactedField
                perm="view_pii"
                value={inspectorName}
                redacted={inspectorName}
                {...(detail.piiRevealed ? { copyable: true } : {})}
              />
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{ color: tokens.text.tertiary }}
              data-testid="report-detail-byline"
            >
              {t('reports.detail.noValue')}
            </Typography>
          )}
          <ReportAcceptanceBadge report={detail} />
          <Typography sx={{ fontFamily: 'monospace', fontSize: 12, color: tokens.text.tertiary }}>
            {shortId(detail.id)}
          </Typography>
        </Box>
      </Box>
      <IconButton onClick={onClose} sx={{ ml: 'auto' }} aria-label={t('reports.detail.close')}>
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};
